import { useMemo } from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import SearchIcon from '@mui/icons-material/SearchOutlined';
import ClearIcon from '@mui/icons-material/CloseOutlined';
import FilterListOffIcon from '@mui/icons-material/FilterListOffOutlined';
import { useData } from '../store/data';
import { useFilters } from '../store/filters';
import { countBy, sortedByCount } from '../lib/filter-rows';
import { useT } from '../i18n';

export function FilterBar() {
  const rows = useData(s => s.rows);
  const f = useFilters();
  const t = useT();

  const assignees = useMemo(() => sortedByCount(countBy(rows, 'Assignee')).map(([n]) => n), [rows]);
  const statuses  = useMemo(() => sortedByCount(countBy(rows, 'Status'))  .map(([n]) => n), [rows]);
  const priorities= useMemo(() => sortedByCount(countBy(rows, 'Priority')).map(([n]) => n), [rows]);

  if (!rows.length) return null;

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        border: 1,
        borderColor: 'divider',
        borderRadius: 3,
        p: 1.5,
      }}
    >
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
        <TextField
          size="small"
          placeholder={t.filters.searchPlaceholder}
          value={f.search}
          onChange={(e) => f.set({ search: e.target.value })}
          sx={{ flex: 2, minWidth: 240 }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
              ),
              endAdornment: f.search ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => f.set({ search: '' })} edge="end">
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            },
          }}
        />

        <Autocomplete
          size="small"
          options={statuses}
          value={f.status || null}
          onChange={(_, v) => f.set({ status: v ?? '' })}
          renderInput={(params) => <TextField {...params} label={t.filters.status} />}
          sx={{ flex: 1, minWidth: 160 }}
        />

        <Autocomplete
          size="small"
          options={assignees}
          value={f.assignee || null}
          onChange={(_, v) => f.set({ assignee: v ?? '' })}
          renderInput={(params) => <TextField {...params} label={t.filters.assignee} />}
          sx={{ flex: 1, minWidth: 160 }}
        />

        <Autocomplete
          size="small"
          options={priorities}
          value={f.priority || null}
          onChange={(_, v) => f.set({ priority: v ?? '' })}
          renderInput={(params) => <TextField {...params} label={t.filters.priority} />}
          sx={{ flex: 1, minWidth: 140 }}
        />

        <Button
          variant="text"
          startIcon={<FilterListOffIcon />}
          onClick={() => f.reset()}
          disabled={!f.hasAnyFilter()}
        >
          {t.filters.reset}
        </Button>
      </Stack>
    </Box>
  );
}
